class WarrantyPlansDrawer extends HTMLElement {
  constructor() {
    super();
    this._currentContext = null;
    this._cache = new Map();
    this._previousFocus = null;
  }

  connectedCallback() {
    this._listEl = this.querySelector('[data-plans-list]');
    this._loadingEl = this.querySelector('[data-loading-state]');
    this._errorEl = this.querySelector('[data-error-state]');
    this._subtitleEl = this.querySelector('[data-parent-context]');

    document.addEventListener('click', this._onDocumentClick = (e) => {
      const cta = e.target.closest('[data-warranty-cta]');
      if (cta) {
        e.preventDefault();
        this._open(cta.dataset);
      }
    });

    this.querySelectorAll('[data-close-drawer]').forEach(el => {
      el.addEventListener('click', () => this._close());
    });

    this.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this._close();
    });
  }

  disconnectedCallback() {
    document.removeEventListener('click', this._onDocumentClick);
  }

  async _open(ctx) {
    this._currentContext = {
      handle: ctx.warrantyProductHandle,
      parentVariantId: parseInt(ctx.parentVariantId, 10),
      parentQuantity: parseInt(ctx.parentQuantity, 10) || 1,
      parentTitle: ctx.parentProductTitle,
    };

    this._previousFocus = document.activeElement;
    this._showState('loading');
    this._setSubtitle(this._currentContext.parentTitle);
    this._showDrawer();

    try {
      const product = await this._fetchProduct(this._currentContext.handle);
      this._renderPlans(product);
      this._showState('list');
    } catch (err) {
      console.error('[warranty-plans-drawer] fetch failed:', err);
      this._showState('error');
    }
  }

  _close() {
    this.setAttribute('aria-hidden', 'true');
    setTimeout(() => {
      this.setAttribute('hidden', '');
      if (this._previousFocus) {
        this._previousFocus.focus();
      }
    }, 300);
  }

  _showDrawer() {
    this.removeAttribute('hidden');
    requestAnimationFrame(() => {
      this.setAttribute('aria-hidden', 'false');
      const closeBtn = this.querySelector('.side-drawer__close');
      if (closeBtn) closeBtn.focus();
    });
  }

  async _fetchProduct(handle) {
    if (this._cache.has(handle)) return this._cache.get(handle);

    const url = `/products/${handle}.js`;
    let product;
    if (window.theme?.fetchCache?.fetch) {
      const res = await window.theme.fetchCache.fetch(url);
      product = typeof res === 'string' ? JSON.parse(res) : res;
    } else {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`Fetch failed: ${res.status}`);
      product = await res.json();
    }

    this._cache.set(handle, product);
    return product;
  }

  _renderPlans(product) {
    const variants = (product.variants || []).filter(v => v.available);
    if (variants.length === 0) {
      this._showState('error');
      return;
    }

    this._listEl.innerHTML = variants.map(v => this._planCardHtml(v, product)).join('');

    this._listEl.querySelectorAll('[data-select-plan]').forEach(btn => {
      btn.addEventListener('click', () => this._selectPlan({
        variantId: btn.dataset.variantId,
        variantTitle: btn.dataset.variantTitle,
      }));
    });
  }

  _planCardHtml(variant, product) {
    const t = (key, fallback) => this._t(key, fallback);
    const title = `${variant.title} ${product.title}`;
    const price = this._formatMoney(variant.price);
    return `
      <article class="warranty-plan-card">
        <h3 class="warranty-plan-card__title">${this._escape(title)}</h3>
        <ul class="warranty-plan-card__features">
          <li>${t('feature_active', 'Active from date of purchase')}</li>
          <li>${t('feature_coverage', 'Covers electrical & mechanical failures')}</li>
        </ul>
        <div class="warranty-plan-card__footer">
          <span class="warranty-plan-card__price">${price}</span>
          <button type="button" class="btn btn--primary"
                  data-select-plan
                  data-variant-id="${variant.id}"
                  data-variant-title="${this._escape(variant.title)}">
            ${t('select', 'Select')}
          </button>
        </div>
      </article>
    `;
  }

  async _selectPlan({ variantId, variantTitle }) {
    const ctx = this._currentContext;
    const button = this.querySelector(`[data-select-plan][data-variant-id="${variantId}"]`);
    if (button) button.disabled = true;

    try {
      const cartAddUrl = window.theme?.routes?.cartAdd || '/cart/add.js';
      const response = await fetch(cartAddUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          items: [{
            id: parseInt(variantId, 10),
            quantity: ctx.parentQuantity,
            properties: {
              '_warranty': 'true',
              '_protected_variant_id': String(ctx.parentVariantId),
              'Plan': variantTitle,
              'Protected Product': ctx.parentTitle,
            },
          }],
        }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.description || `Cart add failed: ${response.status}`);
      }

      this._close();
      // Dispatch refresh event for the cart drawer (silently refreshes its
      // contents via section rendering). cart-drawer.js listens on `document`
      // (not `window`), so dispatch must target the document — otherwise the
      // event never reaches the handler and the drawer doesn't refresh.
      document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));
      // On the /cart page itself, totals don't have a section-rendering refresh
      // in this theme — reload to reflect the new line item + recalculated total.
      // Anywhere else (PDP, collection, etc.) the dispatch event above is
      // sufficient and we must NOT reload, or any open cart drawer disappears.
      if (/\/cart\/?$/.test(window.location.pathname)) {
        window.location.reload();
      }
    } catch (err) {
      console.error('[warranty-plans-drawer] add failed:', err);
      if (button) button.disabled = false;
      this._showInlineError(err.message);
    }
  }

  _showState(state) {
    if (this._loadingEl) this._loadingEl.hidden = state !== 'loading';
    if (this._listEl) this._listEl.hidden = state !== 'list';
    if (this._errorEl) this._errorEl.hidden = state !== 'error';
  }

  _showInlineError(msg) {
    if (!this._errorEl) return;
    const p = this._errorEl.querySelector('p');
    if (p) p.textContent = msg || this._t('error', 'Something went wrong.');
    this._showState('error');
  }

  _setSubtitle(parentTitle) {
    if (!this._subtitleEl) return;
    const tmpl = this._t('drawer_subtitle_for', 'Protection for {{ product }}');
    this._subtitleEl.textContent = tmpl.replace('{{ product }}', parentTitle);
  }

  _t(key, fallback) {
    const strings = window.theme?.strings?.cart?.warranty || {};
    return strings[key] || fallback;
  }

  _escape(s) {
    return String(s).replace(/[&<>"']/g, ch => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]));
  }

  /* Same markup contract as snippets/fx-money.liquid, so gcc-currency.js shows
     GCC shoppers their own currency here too. */
  _formatMoney(cents) {
    let formatted = (cents / 100).toFixed(2);
    if (window.Shopify?.formatMoney) {
      const fmt = window.theme?.settings?.moneyWithCurrencyFormat || '${{amount}}';
      formatted = window.Shopify.formatMoney(cents, fmt);
    }
    return `<span class="tb-fx" data-tb-fx="${parseInt(cents, 10) || 0}">${formatted}</span>`;
  }
}

customElements.define('warranty-plans-drawer', WarrantyPlansDrawer);
